import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import type {
  CustomerAssetSyncStatus,
  CustomerCreatedResponse,
  CustomerDetail,
  CustomerDomainDetail,
  CustomerOnboardingSummary,
  DwdCheckResult,
  DwdCheckStatus,
  DwdScopeCheckResult,
  OnboardingResetResult,
} from '@cdw-ae/common-data';
import type { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';

export type { CustomerAssetSyncStatus, CustomerOnboardingSummary, DwdCheckStatus };

/** Support-console customer-creation request. */
export interface CreateCustomerRequest {
  name: string;
  primaryDomain: string;
  googleId: string;
  schoolDistrict?: string;
  delegateEmail?: string;
  timezone?: string;
  dateFormat?: string;
}

@Injectable({ providedIn: 'root' })
export class PlatformCustomerService {
  private readonly http = inject(HttpClient);

  // The platform-auth interceptor attaches the Bearer token + x-cdw-app-name,
  // so no headers are needed here.
  createCustomer(
    payload: CreateCustomerRequest
  ): Observable<CustomerCreatedResponse> {
    const url = `${environment.urls.backendApi}/platform/customers`;
    return this.http.post<CustomerCreatedResponse>(url, payload);
  }

  getCustomer(id: number): Observable<CustomerDetail> {
    const url = `${environment.urls.backendApi}/platform/customers/${id}`;
    return this.http.get<CustomerDetail>(url);
  }

  /** Onboarding diagnostic: latch, per-step checklist, legal-acceptance log. */
  getOnboardingSummary(id: number): Observable<CustomerOnboardingSummary> {
    const url = `${environment.urls.backendApi}/platform/customers/${id}/onboarding`;
    return this.http.get<CustomerOnboardingSummary>(url);
  }

  /** Full-asset-sync state: running + live counts, plus last-run status/time. */
  getAssetSyncStatus(id: number): Observable<CustomerAssetSyncStatus> {
    const url = `${environment.urls.backendApi}/platform/customers/${id}/asset-sync-status`;
    return this.http.get<CustomerAssetSyncStatus>(url);
  }

  checkDwd(id: number): Observable<DwdCheckResult> {
    const url = `${environment.urls.backendApi}/platform/customers/${id}/check-dwd`;
    return this.http.post<DwdCheckResult>(url, {});
  }

  /**
   * Support diagnostic: which individual scopes the customer has granted.
   * On demand only, and it persists nothing — unlike `checkDwd`, this leaves
   * the tenant's stored DWD status untouched.
   */
  checkDwdScopes(id: number): Observable<DwdScopeCheckResult> {
    const url = `${environment.urls.backendApi}/platform/customers/${id}/check-dwd-scopes`;
    return this.http.post<DwdScopeCheckResult>(url, {});
  }

  /** Support/QA: next SA login at this customer re-enters the onboarding flow. */
  resetOnboarding(id: number): Observable<OnboardingResetResult> {
    const url = `${environment.urls.backendApi}/platform/customers/${id}/reset-onboarding`;
    return this.http.post<OnboardingResetResult>(url, {});
  }

  /**
   * QA (EDUS-2852): clear one user's first-login stamp so their next sign-in is
   * treated as a first entry and lands on the tour page again. Per-user because
   * `resetOnboarding` is customer-scoped and never touches users.
   */
  clearFirstLogin(
    id: number,
    userId: number
  ): Observable<{ firstLoginAt: null }> {
    const url = `${environment.urls.backendApi}/platform/customers/${id}/users/${userId}/clear-first-login`;
    return this.http.post<{ firstLoginAt: null }>(url, {});
  }

  /**
   * Support: force-release the tenant's onboarding session lock (US-12) so a
   * colleague can take over without waiting out the 30-minute expiry.
   */
  releaseOnboardingLock(id: number): Observable<{ released: true }> {
    const url = `${environment.urls.backendApi}/platform/customers/${id}/release-onboarding-lock`;
    return this.http.post<{ released: true }>(url, {});
  }

  /**
   * Support: force the Step-4 sync gate open (EDUS-2858) for a tenant whose
   * sync fails repeatably and who would otherwise never finish onboarding.
   * Stamps the completion timestamp, which beats the Redis run record.
   */
  completeInitialSync(id: number): Observable<{ completedAt: string }> {
    const url = `${environment.urls.backendApi}/platform/customers/${id}/complete-initial-sync`;
    return this.http.post<{ completedAt: string }>(url, {});
  }

  /**
   * Update the customer's DWD delegate email (support/QA). Invalidates the
   * stored DWD check server-side; the next login re-verifies.
   */
  updateDelegate(
    id: number,
    delegateEmail: string
  ): Observable<{ delegateEmail: string }> {
    const url = `${environment.urls.backendApi}/platform/customers/${id}/delegate`;
    return this.http.post<{ delegateEmail: string }>(url, { delegateEmail });
  }

  /**
   * Support (EDUS-2864): add an authorized domain to an already-onboarded
   * customer. Same validation as the wizard's Step 2; the "already assigned"
   * conflict additionally names the owning customer so support can act on it.
   */
  addDomain(id: number, domain: string): Observable<CustomerDomainDetail> {
    const url = `${environment.urls.backendApi}/platform/customers/${id}/domains`;
    return this.http.post<CustomerDomainDetail>(url, { domain });
  }

  /**
   * Support: remove an authorized domain. The server refuses the primary
   * (promote a replacement first) and the service account's own domain.
   */
  removeDomain(id: number, domainId: number): Observable<{ removed: true }> {
    const url = `${environment.urls.backendApi}/platform/customers/${id}/domains/${domainId}`;
    return this.http.delete<{ removed: true }>(url);
  }

  /**
   * Support: promote an existing authorized domain to primary. The outgoing
   * primary demotes to a secondary and can then be removed separately.
   */
  makePrimaryDomain(
    id: number,
    domainId: number
  ): Observable<{ primaryDomain: string }> {
    const url = `${environment.urls.backendApi}/platform/customers/${id}/domains/${domainId}/make-primary`;
    return this.http.post<{ primaryDomain: string }>(url, {});
  }

  /** Super-only; corrects the stored Google customer id after an id_mismatch. */
  correctGoogleId(
    id: number,
    googleId: string
  ): Observable<{ id: number; googleId: string }> {
    const url = `${environment.urls.backendApi}/platform/customers/${id}/correct-google-id`;
    return this.http.post<{ id: number; googleId: string }>(url, { googleId });
  }
}
